import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Coins, Flame, FolderOpen, Sparkles, Layers } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { useSite } from '../context/SiteContext';
import { getLocalizedText } from '../components/MultiLanguageInput';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CategoryDetail = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { settings: siteSettings } = useSite();
  const [category, setCategory] = useState(null);
  const [pools, setPools] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Current language for multi-language content
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    fetchCategoryAndPools();
  }, [slug]);

  const fetchCategoryAndPools = async () => {
    try {
      // Fetch category info
      const catRes = await axios.get(`${API}/categories`);
      const foundCategory = catRes.data.find(c => c.slug === slug);
      setCategory(foundCategory);

      // Fetch pools for this category
      if (foundCategory) {
        const poolsRes = await axios.get(`${API}/pools`);
        // Filter pools by category and sort by created_at descending (newest first)
        const categoryPools = poolsRes.data
          .filter(p => p.category_id === foundCategory.id)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setPools(categoryPools);

        // Fetch subcategories for this category
        const subRes = await axios.get(`${API}/subcategories?category_id=${foundCategory.id}`);
        setSubcategories(subRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch category:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter pools by selected subcategory
  const filteredPools = selectedSubcategory
    ? pools.filter(p => p.subcategory_id === selectedSubcategory)
    : pools;

  const getIconUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex flex-col items-center justify-center px-4">
        <FolderOpen className="w-16 h-16 text-zinc-600 mb-4" />
        <h2 className="text-[#1A1206] text-lg font-bold mb-2">{t('category.notFound')}</h2>
        <p className="text-zinc-500 text-sm mb-4">{t('category.notFoundDesc')}</p>
        <button
          onClick={() => navigate('/home')}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold"
        >
          {t('category.backToHome')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-20">
      {/* SEO from category data */}
      <SEOHead 
        customTitle={siteSettings?.app_name ? `${getLocalizedText(category.name, currentLang)} | ${siteSettings.app_name}` : getLocalizedText(category.name, currentLang)}
        customDescription={getLocalizedText(category.description, currentLang) || ''}
      />
      
      {/* Sticky Header - Only category name */}
      <div className="sticky top-0 z-40">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950 to-amber-950/95 pointer-events-none" />
        
        <div className="relative px-4 py-3 flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center border border-white/10"
          >
            <ArrowLeft className="w-5 h-5 text-[#1A1206]" />
          </motion.button>
          
          {/* Category Icon & Name */}
          <div className="flex items-center gap-2.5 flex-1">
            {category.icon_url ? (
              <img 
                src={getIconUrl(category.icon_url)} 
                alt={getLocalizedText(category.name, currentLang)}
                className="w-9 h-9 rounded-lg object-contain bg-amber-900/50 p-1"
              />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-amber-900/50 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-amber-300" />
              </div>
            )}
            <div>
              <h1 className="text-[#1A1206] font-bold text-base font-['Chakra_Petch']">{getLocalizedText(category.name, currentLang)}</h1>
              <p className="text-amber-200/60 text-[10px]">
                {selectedSubcategory 
                  ? t('category.filteredCollections', { count: filteredPools.length, total: pools.length, defaultValue: `${filteredPools.length} of ${pools.length} collections` })
                  : t('category.collectionsAvailable', { count: pools.length })
                }
              </p>
            </div>
          </div>
        </div>
        
        {/* Bottom line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      </div>

      {/* Description - Scrolls with content */}
      {getLocalizedText(category.description, currentLang) && (
        <div className="px-4 py-3 bg-amber-950/30">
          <p className="text-amber-100/70 text-sm leading-relaxed">
            {getLocalizedText(category.description, currentLang)}
          </p>
        </div>
      )}

      {/* Subcategory Filter */}
      {subcategories.length > 0 && (
        <div className="px-4 py-3 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2">
            {/* All button */}
            <button
              onClick={() => setSelectedSubcategory(null)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                !selectedSubcategory
                  ? 'bg-amber-600 border-amber-500 text-white shadow-lg shadow-amber-500/20'
                  : 'bg-amber-900/30 border-amber-700/50 text-amber-200 hover:border-amber-600'
              }`}
            >
              <span className="text-sm font-medium">{t('common.all', { defaultValue: 'All' })}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                !selectedSubcategory ? 'bg-white/20' : 'bg-amber-800/50'
              }`}>
                {pools.length}
              </span>
            </button>
            
            {/* Subcategory buttons */}
            {subcategories.map((sub) => {
              const count = pools.filter(p => p.subcategory_id === sub.id).length;
              return (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSubcategory(sub.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    selectedSubcategory === sub.id
                      ? 'bg-amber-600 border-amber-500 text-white shadow-lg shadow-amber-500/20'
                      : 'bg-amber-900/30 border-amber-700/50 text-amber-200 hover:border-amber-600'
                  }`}
                >
                  {sub.image_url ? (
                    <img src={sub.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <Layers className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">{getLocalizedText(sub.name, currentLang)}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    selectedSubcategory === sub.id ? 'bg-white/20' : 'bg-amber-800/50'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pools Grid */}
      <div className="px-4 py-4">
        {filteredPools.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <h3 className="text-[#1A1206] font-semibold mb-1">
              {selectedSubcategory ? t('category.noPoolsInSubcategory', { defaultValue: 'No pools in this subcategory' }) : t('category.noCollectionsYet')}
            </h3>
            <p className="text-zinc-500 text-sm">{t('category.comingSoon')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
            {filteredPools.map((pool, index) => {
              const pct = pool.total_quantity > 0 ? (pool.remaining_quantity / pool.total_quantity) * 100 : 0;
              return (
              <motion.div
                key={pool.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(`/pool/${pool.id}`)}
                className="group relative rounded-xl overflow-hidden cursor-pointer border border-[#FFC300]/15 bg-white hover:border-[#FFC300]/70 hover:shadow-[0_0_22px_-6px_rgba(113,223,231,0.55)] transition-all duration-300"
                data-testid={`category-pool-${pool.id}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img 
                    src={pool.image_url} 
                    alt={getLocalizedText(pool.name, currentLang)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#04161e] via-[#04161e]/35 to-transparent" />

                  {/* Price badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#04161e]/80 backdrop-blur-md border border-[#FFC300]/30 px-2 py-0.5 rounded-full">
                    <Coins className="w-3 h-3 text-yellow-400" />
                    <span className="text-white font-bold text-[11px]">{pool.price}</span>
                  </div>

                  {/* New badge */}
                  {isNewPool(pool.created_at) && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-[#FFC300] rounded-full text-[9px] font-bold text-[#04161e]">
                      {t('common.new').toUpperCase()}
                    </div>
                  )}

                  {/* Bottom overlay info */}
                  <div className="absolute bottom-0 left-0 right-0 p-2.5">
                    <h4 className="text-white font-bold text-xs sm:text-sm leading-tight line-clamp-2 mb-1.5">{getLocalizedText(pool.name, currentLang)}</h4>
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-3 h-3 text-orange-400 shrink-0" />
                      <div className="flex-1 h-1 bg-white/15 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FFD400] to-[#FF8C00] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[#6E6452] text-[9px] font-medium shrink-0">{pool.remaining_quantity || 0}/{pool.total_quantity || 0}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to check if pool is new (within 7 days)
const isNewPool = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
  return diffDays <= 7;
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('id-ID', options);
};

export default CategoryDetail;
